import React from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, TruckIcon, XIcon } from 'lucide-react';
import { Product, Quote } from '../types/catalog';
import { formatCurrency, formatMiles } from '../utils/geo';
import { loadTotal, priceUnit } from '../utils/quotes';
interface QuoteRowProps {
  quote: Quote;
  product: Product;
  /** Load size to price out, in the product's bulk unit. */
  loadUnits: number;
  isBest: boolean;
  index: number;
  premiumVsBest: number;
  beyondRadius: boolean;
  onRemoveManual: (entryId: string) => void;
}
export function QuoteRow({
  quote,
  product,
  loadUnits,
  isBest,
  index,
  premiumVsBest,
  beyondRadius,
  onRemoveManual
}: QuoteRowProps) {
  const {
    quarry,
    price,
    distanceMiles,
    haulCost
  } = quote;
  const isManual = price.source === 'manual';
  const landed = price.price + quarry.surchargePerUnit;
  const load = loadTotal(quote, product, loadUnits);
  const unit = priceUnit(price, product);
  return <motion.li initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.22,
    delay: Math.min(index, 6) * 0.04,
    ease: [0.23, 1, 0.32, 1]
  }} className={`grid grid-cols-12 items-start gap-x-4 gap-y-3 border-b border-line px-5 ${isBest ? 'bg-accent-soft py-5' : 'bg-surface py-4'}`} data-id="element-138" data-mp="2v4ccjzn9kf7w">
      <div className="col-span-12 md:col-span-5" data-id="element-139" data-mp="2juap3qmkruu0">
        <div className="flex flex-wrap items-center gap-2" data-id="element-140" data-mp="2jx6o7ia5spk1">
          <h3 className={`font-semibold text-ink ${isBest ? 'text-lg' : 'text-base'}`} data-id="element-141" data-mp="262nqhk55ua82">
            {quarry.name}
          </h3>
          <span className="tnum text-xs text-ink-soft" data-id="element-142" data-mp="2avqgjiaqb1ji">{quarry.siteCode}</span>
          {isBest && <span className="rounded bg-accent px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white" data-id="element-143" data-mp="1w3erudzco7fd">
              Best price
            </span>}
          {isManual && <span className="rounded border border-ink/20 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-144" data-mp="1n7754twdu8n7">
              Manual
            </span>}
          {beyondRadius && <span className="rounded border border-warn/40 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-warn" data-id="element-145" data-mp="3p2n3coqpgmn1">
              Outside radius
            </span>}
        </div>
        <p className="text-sm text-ink-muted" data-id="element-146" data-mp="3f4pfi2crcruy">{quarry.operator}</p>
        <address className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm not-italic text-ink-muted" data-id="element-147" data-mp="3f24d9c8js9wo">
          <span data-id="element-148" data-mp="bngzlo1lc22v">
            {quarry.address ? `${quarry.address}, ` : ''}
            {quarry.city}, {quarry.state}
            {quarry.zip ? ` ${quarry.zip}` : ''}
          </span>
          {quarry.phone && <a href={`tel:${quarry.phone.replace(/[^\d]/g, '')}`} className="inline-flex items-center gap-1 transition-colors duration-150 hover:text-accent" data-id="element-149" data-mp="2n29mbdolyt1">
              <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-150" data-mp="2nchyv1x95fgk" />
              {quarry.phone}
            </a>}
        </address>
        {price.note && <p className="mt-1 text-sm italic text-ink-muted" data-id="element-151" data-mp="12wtk503cposv">{price.note}</p>}
      </div>

      <div className="col-span-4 md:col-span-2" data-id="element-152" data-mp="16x3pqbn8ed77">
        <p className="text-[11px] uppercase tracking-wide text-ink-soft md:hidden" data-id="element-153" data-mp="1bvx5ybtwkib2">Distance</p>
        <p className="tnum text-base font-medium leading-6 text-ink" data-id="element-154" data-mp="1gm3ts9glbys9">
          {formatMiles(distanceMiles)} mi
        </p>
        <p className="tnum mt-0.5 flex items-center gap-1 text-xs text-ink-muted" data-id="element-155" data-mp="1lm9s28hd8jjb">
          <TruckIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-156" data-mp="1qk1qzmgfh5x0" />
          {price.deliveryFee ? 'haul in price' : `~${formatCurrency(haulCost, 0)} haul`}
        </p>
      </div>

      <div className="col-span-4 md:col-span-2" data-id="element-157" data-mp="1zkzmfgcsw53z">
        <p className="text-[11px] uppercase tracking-wide text-ink-soft md:hidden" data-id="element-158" data-mp="1zkpv9pg0rrwp">Per {unit}</p>
        <p className="tnum text-base font-medium leading-6 text-ink" data-id="element-159" data-mp="9oqctnme25i">
          {formatCurrency(price.price)}
        </p>
        <p className="tnum mt-0.5 text-xs text-ink-muted" data-id="element-160" data-mp="59b3z47d1keu">
          {quarry.surchargePerUnit > 0 ? `${formatCurrency(landed)} landed` : `per ${unit}`}
          {premiumVsBest > 0 && <span className="text-warn" data-id="element-161" data-mp="3133asyrkr8ym"> · +{formatCurrency(premiumVsBest)}</span>}
        </p>
        {price.smallLoadPrice && <p className="tnum text-xs text-ink-soft" data-id="element-162" data-mp="3b1sixzto5f1o">
            {formatCurrency(price.smallLoadPrice)} {price.smallLoadLabel ?? 'under 3.5 t'}
          </p>}
      </div>

      <div className="col-span-4 flex items-start justify-end gap-2 md:col-span-3" data-id="element-163" data-mp="2n9lpp9j8po6h">
        <div className="text-right" data-id="element-164" data-mp="2iaqybj77irar">
          <p className="tnum text-[11px] uppercase tracking-wide text-ink-soft md:hidden" data-id="element-165" data-mp="2x29mi4a7zs57">
            {load.quantity} {load.unit} load
          </p>
          <p className="tnum inline-block rounded-md bg-accent/10 px-2 py-0.5 text-lg font-semibold leading-6 text-accent" data-id="element-166" data-mp="2s9q8x7bj8x0e">
            {formatCurrency(load.total, 0)}
          </p>
          <p className="tnum mt-0.5 text-xs text-ink-muted" data-id="element-167" data-mp="1zlkpcnw7te25">
            {load.minimumApplied ? `${formatCurrency(price.minLoadCharge, 0)} load minimum` : load.surcharge > 0 ? `incl. ${formatCurrency(load.surcharge, 0)} surcharge` : load.delivery > 0 ? `incl. ${formatCurrency(load.delivery, 0)} delivery` : 'material only'}
          </p>
        </div>
        {isManual && price.entryId && <button type="button" onClick={() => onRemoveManual(price.entryId as string)} aria-label={`Remove manual price from ${quarry.name}`} className="rounded p-1 text-ink-soft transition-colors duration-150 hover:bg-line hover:text-ink" data-id="element-168" data-mp="xvul18tl3sl9">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-169" data-mp="suolos5f572r" />
          </button>}
      </div>
    </motion.li>;
}